/**
 * Countdown Timer Component
 * Handles countdown display and auto-hide when expired
 * End time is interpreted as AWST (Australian Western Standard Time, UTC+8)
 */

class CountdownTimer {
  #timerBlock;
  #endTime;
  #intervalId;
  #blockId;

  constructor(timerBlock) {
    this.#timerBlock = timerBlock;
    this.#blockId = timerBlock.dataset.blockId;
    const endTimeString = timerBlock.dataset.endTime;

    if (!endTimeString) {
      this.#hide();
      return;
    }

    // Parse the date string as AWST (UTC+8)
    this.#endTime = this.#parseAWSTDate(endTimeString);

    if (!this.#endTime || isNaN(this.#endTime.getTime())) {
      console.error(
        `Countdown Timer: Invalid end time format for block ${this.#blockId}. Expected format: YYYY-MM-DDTHH:MM:SS (interpreted as AWST)`
      );
      this.#hide();
      return;
    }

    this.#init();
  }

  /**
   * Parse a date string as AWST (UTC+8)
   * @param {string} dateString - Date string in format YYYY-MM-DDTHH:MM:SS
   * @returns {Date} - Date object representing the AWST time
   */
  #parseAWSTDate(dateString) {
    // Append AWST timezone offset (+08:00) to the date string
    // This ensures the date is parsed as AWST time
    const awstDateString = dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
      ? dateString
      : `${dateString}+08:00`;

    const date = new Date(awstDateString);

    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  #init() {
    // Check if already expired on load
    if (this.#isExpired()) {
      this.#hide();
      return;
    }

    // Show the timer block
    this.#timerBlock.hidden = false;

    // Update immediately
    this.#update();

    // Update every second
    this.#intervalId = setInterval(() => {
      if (this.#isExpired()) {
        this.#hide();
        this.#cleanup();
        return;
      }

      this.#update();
    }, 1000);
  }

  #isExpired() {
    return new Date() >= this.#endTime;
  }

  #update() {
    const now = new Date();
    const diff = this.#endTime - now;

    if (diff <= 0) {
      this.#hide();
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.#updateDisplay(days, hours, minutes, seconds);
  }

  #updateDisplay(days, hours, minutes, seconds) {
    const daysEl = this.#timerBlock.querySelector('[data-unit="days"]');
    const hoursEl = this.#timerBlock.querySelector('[data-unit="hours"]');
    const minutesEl = this.#timerBlock.querySelector('[data-unit="minutes"]');
    const secondsEl = this.#timerBlock.querySelector('[data-unit="seconds"]');

    if (daysEl) {
      daysEl.textContent = String(days).padStart(2, '0');
    }

    if (hoursEl) {
      hoursEl.textContent = String(hours).padStart(2, '0');
    }

    if (minutesEl) {
      minutesEl.textContent = String(minutes).padStart(2, '0');
    }

    if (secondsEl) {
      secondsEl.textContent = String(seconds).padStart(2, '0');
    }
  }

  #hide() {
    this.#timerBlock.hidden = true;
  }

  #cleanup() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  destroy() {
    this.#cleanup();
  }
}

// Initialize all countdown timers on the page
document.addEventListener('DOMContentLoaded', () => {
  const timerBlocks = document.querySelectorAll('.countdown-timer-block');
  const timers = [];

  timerBlocks.forEach((block) => {
    // Hide by default, will be shown if not expired
    block.hidden = true;
    timers.push(new CountdownTimer(block));
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    timers.forEach((timer) => timer.destroy());
  });
});

